import { useCallback, useEffect, useRef } from 'preact/hooks'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'
import { SearchspringSuggestProvider } from 'libs/searchspring/fetchers/suggest'
import { SearchspringTrendingProvider } from 'libs/searchspring/fetchers/trending'
import { SearchPageLink } from 'libs/searchspring/components/quick-search/SearchPageLink'
import { QuickSearchSuggestions } from 'libs/searchspring/components/quick-search/QuickSearchSuggestions'
import { QuickSearchResults } from 'libs/searchspring/components/quick-search/QuickSearchResults'
import clsx from 'clsx'
import { globalState } from '@/components/helpers/utils'
import { SkeletonCard } from 'libs/searchspring/components/SkeletonCard'
import { useSearchResultState } from '../../hooks/useSearchResultState'
import { withRecommendationProvider } from 'libs/searchspring/components/recommendations/withRecommendationsProvider'
import { CarouselResults } from 'libs/searchspring/components/recommendations/CarouselResults'

/**
 * Gets popular categories HTML content from the DOM
 * @returns {string|undefined} HTML content for popular categories or undefined if not found
 */
const getPopularCategories = () => {
  const popularCategoryWrapper = document.getElementById('popular-categories')
  return popularCategoryWrapper?.innerHTML || undefined
}

// Extract and filter default suggestions from global state
const defaultSuggestions = globalState.search?.suggestions || []
const collectionsSuggestions = defaultSuggestions.filter(s => s.type === 'collections') || []
const pagesSuggestions = defaultSuggestions.filter(s => s.type === 'pages') || []
const popularProductsProfile = globalState.siteIds?.popularProductsProfile || 'api-test'

// Configuration for different search states
export const SEARCH_CONFIG = {
  NO_SEARCH: {
    suggestions: [
      { title: 'Popular Collections', type: 'collections', default: collectionsSuggestions, isStatic: true },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: 'Popular Categories'
  },
  NO_RESULTS: {
    suggestions: [
      { title: 'Popular Searches' },
      { title: 'Popular Collections', type: 'collections', default: collectionsSuggestions, isStatic: true }
    ],
    mainTitle: 'Popular Products'
  },
  HAS_RESULTS: {
    suggestions: [
      { title: 'Suggested Searches' },
      { title: 'Popular Collections', type: 'collections', default: collectionsSuggestions, isStatic: true }
    ],
    mainTitle: 'Search Results'
  },
  REDIRECT: {
    mainTitle: 'Suggested Pages'
  }
}

/**
 * ViewResultsButton component - Renders the "View Results" button for search results
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @param {number} props.totalResults - Total number of search results
 * @param {boolean} props.isMobile - Whether to show mobile-specific styling
 * @returns {JSX.Element} Rendered component
 */
export const ViewResultsButton = ({ query, totalResults, isMobile = false }) => {
  if (!query || !totalResults) return null

  const className = isMobile
    ? 'lg:!hidden btn btn-primary btn-fill btn-m w-full'
    : 'shrink-0 lg-max:!hidden btn btn-primary btn-fill btn-l w-fit'

  return (
    <SearchPageLink query={query} className={className}>
      View {totalResults} Results
    </SearchPageLink>
  )
}

/**
 * QuickSearchDropdown component - Displays search results and suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @returns {JSX.Element} Rendered component
 */
export function QuickSearchDropdown({ query, redirect, setRedirect, onResultsUpdate }) {
  const popularCategories = useRef(getPopularCategories())
  const { refine, results, isLoading } = useSearchspringContext()
  const { title, hasResults, showNoResults, isCheckingResults, isRecommendationsReady, isResultsReady } = useSearchResultState({ query, results, isLoading })
  const Recommendations = useCallback(withRecommendationProvider(CarouselResults, 'recommend'), [])

  const itemClass = 'w-grid-5/8 lg:w-full max-w-[280px] md:max-w-grid-1/3 mr-gutter [_&>div]:w-full'
  const productCardSettings = {
    hideQuickAdd: true,
    hideSwatches: false,
    hidePromolines: false,
    hideBadges: false,
    hideWishlist: false
  }
  const carouselSettings = {
    draggable: true,
    freeScroll: false,
    wrapAround: false,
    groupCells: true,
    fade: false,
    adaptiveHeight: false,
    accessibility: true,
    cellAlign: 'left',
    prevNextButtons: false,
    pageDots: false
  }

  // Update search query when query prop changes
  useEffect(() => {
    refine('query', query)
  }, [query])

  useEffect(() => {
    if (results?.merchandising?.redirect) {
      setRedirect(results?.merchandising?.redirect)
    } else {
      setRedirect(false)
    }
  }, [results?.merchandising?.redirect])

  useEffect(() => {
    if (onResultsUpdate && results) {
      const hasResults = results?.pagination?.totalResults > 0
      const totalResults = results?.pagination?.totalResults || 0
      onResultsUpdate({ hasResults, totalResults })
    }
  }, [results, onResultsUpdate])

  /**
   * Renders suggestion components based on current search state
   * @returns {JSX.Element[]} Array of SearchSuggestions components
   */
  const renderSuggestions = () => {
    let config
    if (query && hasResults) {
      config = SEARCH_CONFIG.HAS_RESULTS
    } else if (query && !hasResults) {
      config = SEARCH_CONFIG.NO_RESULTS
    } else {
      config = SEARCH_CONFIG.NO_SEARCH
    }
    return config.suggestions.map((suggestion, index) => (
      <SearchSuggestions
        key={index}
        title={suggestion.title}
        type={suggestion.type}
        query={query && !hasResults ? '' : query}
        defaultSuggestions={suggestion.default}
        isStatic={suggestion.isStatic}
      />
    ))
  }

  /**
   * Renders the main content section based on search results
   * @returns {JSX.Element|null} QuickSearchResults or PopularCategories component
   */
  return (
    <div className="grid lg:grid-cols-main lg:auto-rows-auto gap-x-gutter max-h-[75vh] bg-secondary lg:px-margin overflow-y-scroll no-scrollbar overscroll-none">
      {/* Main Content Section */}
      <div className="shrink-0 lg:col-[3/-1] lg:row-[2/3] flex flex-col gap-y-1.5 w-full lg:w-full-margin-half h-fit lg:h-full px-margin lg:pl-2 py-1.5 lg:py-2.5 lg-max:border-b lg:border-l border-neutral-light overflow-x-hidden">
        <h2 className="paragraph-l font-bold">{title}</h2>

        <div className="w-full">
          {/* Loading Overlay */}
          <div className={clsx('w-full overflow-hidden', (isCheckingResults || isLoading) ? '' : 'h-0 opacity-0 pointer-events-none')}>
            <SkeletonGrid carouselSettings={carouselSettings} itemClass={itemClass} />
          </div>
          <div className={clsx('w-full', (hasResults) ? '' : 'hidden')}>
            {isResultsReady && (
              <QuickSearchResults />
            )}
          </div>
          <div className={clsx('w-full', (!hasResults && query && !isLoading && showNoResults) ? '' : 'hidden')} data-dl-section-id="api-test">
            {isRecommendationsReady && (
              <Recommendations profile={popularProductsProfile} limit={4} itemClass={itemClass} productCardSettings={productCardSettings} carouselSettings={carouselSettings} />
            )}
          </div>
          <div className={clsx('w-full', query ? 'hidden' : '')}>
            {popularCategories.current && <PopularCategories content={popularCategories.current} carouselSettings={carouselSettings} />}
          </div>
        </div>

        {/* Mobile "View results" button */}
        {(hasResults && !redirect) && (
          <ViewResultsButton query={query} totalResults={results?.pagination?.totalResults} isMobile={true} />
        )}
      </div>

      {/* Suggestions Section */}
      <div className="shrink-0 lg:col-[1/3] lg:row-[2/3] flex flex-col gap-y-1.5 lg:gap-y-2.5 w-full lg:w-full-margin-half-gutter h-fit lg:h-full lg:-ml-margin py-1.5 lg:py-2.5">
        {renderSuggestions()}
      </div>

      {/* No Results Section */}
      {(!hasResults && query && !isLoading && showNoResults) && (
        <div class="shrink-0 lg:col-span-full row-[1] flex flex-col w-full lg:w-full-margin px-margin py-1 lg:-ml-margin bg-system-error-light">
          <span className="paragraph-b">No results for "{query}". Please try another search term.</span>
        </div>
      )}
    </div>
  )
}

/**
 * PopularCategories component - Renders HTML content for popular categories
 * @param {Object} props - Component props
 * @param {string} props.content - HTML content to render
 * @returns {JSX.Element} Rendered component
 */
const PopularCategories = ({ content, carouselSettings }) => {
  return (
    <x-carousel
      className="flex w-full h-fit"
      carousel-settings={JSON.stringify(carouselSettings)}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  )
}

/**
 * SkeletonGrid component - Renders a loading state with skeleton cards
 * @param {Object} props - Component props
 * @param {Object} props.carouselSettings - Settings for the carousel
 * @param {string} props.itemClass - CSS class for carousel items
 * @returns {JSX.Element} Rendered component
 */
const SkeletonGrid = ({ carouselSettings, itemClass }) => {
  return (
    <x-carousel
      className="flex w-full h-fit"
      carousel-settings={JSON.stringify(carouselSettings)}
    >
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
    </x-carousel>
  )
}

/**
 * SearchSuggestions component - Renders search suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @param {string} props.type - Type of suggestions ('collections' or 'pages')
 * @param {string} props.title - Title text for the suggestions section
 * @param {Array} props.defaultSuggestions - Default suggestions to display
 * @returns {JSX.Element} Rendered component
 */
const SearchSuggestions = ({ query, type, title, defaultSuggestions, isStatic = false }) => {
  return (
    <div className="flex flex-col gap-y-1.875 relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:border-t after:border-neutral-light px-margin pb-1.875 lg:pb-2.5 last:after:hidden last:pb-0">
      <h3 className="paragraph-l font-bold">{title}</h3>
      {isStatic ? (
        <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} />
      ) : (
        query ? (
        <SearchspringSuggestProvider category={type}>
          <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} />
        </SearchspringSuggestProvider>
        ) : (
        <SearchspringTrendingProvider category={type}>
          <QuickSearchSuggestions defaultSuggestions={defaultSuggestions} />
        </SearchspringTrendingProvider>
        )
      )}
    </div>
  )
}
